'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import { Truck, Building2, ArrowRight, Mail } from 'lucide-react';
import { toast } from 'sonner';

export default function LandingFooter() {
  const [email, setEmail] = useState('');

  const handleEmailCapture = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    // TODO: Backend — POST /api/leads with { email, timestamp }
    toast.success('You\'re on the list!', { description: 'We\'ll notify you about new platform features.' });
    setEmail('');
  };

  return (
    <footer className="bg-zinc-900 border-t border-zinc-800">
      {/* CTA Banner */}
      <div className="bg-gradient-to-r from-amber-500/10 via-orange-500/10 to-amber-500/10 border-b border-amber-500/20">
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 py-16 text-center">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">
            Ready to Connect Smarter?
          </h2>
          <p className="text-zinc-400 mb-8 max-w-lg mx-auto">
            Join thousands of CDL drivers and top carriers already using HireTruckers365.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              href="/sign-up-login-screen"
              className="group flex items-center gap-3 px-8 py-4 rounded-xl bg-amber-500 text-zinc-950 font-bold hover:bg-amber-400 active:scale-95 transition-all duration-150 w-full sm:w-auto justify-center"
            >
              <Truck size={18} />
              Apply as a Driver — Free
              <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              href="/sign-up-login-screen"
              className="group flex items-center gap-3 px-8 py-4 rounded-xl bg-zinc-800 text-zinc-100 font-bold border border-zinc-700 hover:bg-zinc-700 active:scale-95 transition-all duration-150 w-full sm:w-auto justify-center"
            >
              <Building2 size={18} />
              Post a Driver Position
              <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </div>

      {/* Footer links */}
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 py-12">
        <div className="grid md:grid-cols-4 gap-8 mb-10">
          <div>
            <div className="flex items-center gap-2 mb-4">
              <AppLogo size={28} />
              <span className="font-bold text-zinc-100">
                HireTruckers<span className="text-amber-400">365</span>
              </span>
            </div>
            <p className="text-xs text-zinc-500 leading-relaxed">
              The private trucking recruitment marketplace. Admin-controlled introductions. Zero spam.
            </p>
          </div>

          <div>
            <p className="section-header mb-4">For Drivers</p>
            <div className="space-y-2">
              {[
                { label: 'Create Profile', href: '/sign-up-login-screen' },
                { label: 'How Matching Works', href: '/sign-up-login-screen' },
                { label: 'Driver FAQ', href: '/sign-up-login-screen' },
                { label: 'Privacy Policy', href: '/legal?section=privacy' },
              ].map((item) => (
                <Link key={`driver-link-${item.label}`} href={item.href} className="block text-sm text-zinc-500 hover:text-zinc-300 transition-colors">
                  {item.label}
                </Link>
              ))}
            </div>
          </div>

          <div>
            <p className="section-header mb-4">For Carriers</p>
            <div className="space-y-2">
              {[
                { label: 'Post a Job', href: '/sign-up-login-screen' },
                { label: 'Referral Fee Agreement', href: '/legal?section=fees' },
                { label: 'Employer FAQ', href: '/sign-up-login-screen' },
                { label: 'Terms of Service', href: '/legal?section=terms' },
              ].map((item) => (
                <Link key={`carrier-link-${item.label}`} href={item.href} className="block text-sm text-zinc-500 hover:text-zinc-300 transition-colors">
                  {item.label}
                </Link>
              ))}
            </div>
          </div>

          <div>
            <p className="section-header mb-4">Stay Updated</p>
            <p className="text-xs text-zinc-500 mb-3">Get platform updates and trucking industry news.</p>
            <form onSubmit={handleEmailCapture} className="flex gap-2">
              <div className="relative flex-1">
                <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg pl-8 pr-3 py-2 text-xs text-zinc-100 placeholder-zinc-600 focus:outline-none focus:ring-1 focus:ring-amber-500/50"
                />
              </div>
              <button type="submit" className="btn-primary text-xs px-3 py-2">
                <ArrowRight size={14} />
              </button>
            </form>
          </div>
        </div>

        <div className="border-t border-zinc-800 pt-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-xs text-zinc-600">
            © 2026 HireTruckers365. All rights reserved.
          </p>
          <div className="flex items-center gap-4">
            <Link href="/legal?section=terms" className="text-xs text-zinc-600 hover:text-zinc-400 transition-colors">Terms</Link>
            <Link href="/legal?section=privacy" className="text-xs text-zinc-600 hover:text-zinc-400 transition-colors">Privacy</Link>
            <Link href="/legal?section=fees" className="text-xs text-zinc-600 hover:text-zinc-400 transition-colors">Fee Schedule</Link>
            <Link href="/legal?section=affiliate" className="text-xs text-zinc-600 hover:text-zinc-400 transition-colors">Affiliate</Link>
          </div>
          <p className="text-xs text-zinc-600">
            A 20% referral fee applies to all hires made through introductions on this platform.
          </p>
        </div>
      </div>
    </footer>
  );
}